import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface FulfillmentItem {
  asset_request_item_id: string;
  fulfillment_type: 'inventory' | 'procurement' | 'transfer';
  quantity: number;
  asset_id?: string;
  transfer_request_id?: string;
  transfer_details?: {
    asset_id: string;
    source_subsidiary_id: string;
    source_location_id: string;
    destination_subsidiary_id: string;
    destination_location_id: string;
    destination_department_id?: string;
    transfer_type?: string;
    transfer_mode?: string;
    requested_date?: string;
    loan_return_date?: string;
    expected_delivery_date?: string;
    delivery_challan_no?: string;
    remarks?: string;
  };
  assigned_to_user_id?: string;
  assigned_to_department_id?: string;
  item_master_id?: string;
  remarks?: string;
}

export interface CreateFulfillmentPayload {
  fulfillments: FulfillmentItem[];
}

export interface TransferRequestPayload {
  asset_id: string;
  source_subsidiary_id: string;
  source_location_id: string;
  destination_subsidiary_id: string;
  destination_location_id: string;
  destination_department_id?: string;
  asset_request_id?: string;
  asset_request_item_id?: string;
  transfer_type?: string;
  transfer_mode?: string;
  requested_date?: string;
  loan_return_date?: string;
  expected_delivery_date?: string;
  delivery_challan_no?: string;
  remarks?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AssetFulfillmentService {
  private assetUrl = environment.assetUrl;

  constructor(private http: HttpClient) {}

  // ============================================
  // FORM TEMPLATES
  // ============================================

  getFormTemplates(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-fulfillment-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getFormTemplateById(id: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-fulfillment-form-templates/${id}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getDefaultFormTemplate(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-fulfillment-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // ============================================
  // FULFILLMENT APIs
  // ============================================

  createFulfillment(requestId: string, body: CreateFulfillmentPayload): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-requests/${requestId}/fulfill`;
    return this.http.post<any>(url, body, { withCredentials: true });
  }

  getFulfillments(requestId: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-requests/${requestId}/fulfillments`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getFulfillment(fulfillmentId: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-request-fulfillments/${fulfillmentId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  updateFulfillment(fulfillmentId: string, body: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-request-fulfillments/${fulfillmentId}`;
    return this.http.put<any>(url, body, { withCredentials: true });
  }

  deleteFulfillment(fulfillmentId: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-request-fulfillments/${fulfillmentId}`;
    return this.http.delete<any>(url, { withCredentials: true });
  }

  // ============================================
  // TRANSFER APIs
  // ============================================

  createTransfer(body: TransferRequestPayload): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/`;
    return this.http.post<any>(url, body, { withCredentials: true });
  }

  // ============================================
  // SUPPORTING LOOKUPS
  // ============================================

  getAvailableAssets(filters: {
    status?: string;
    asset_group_id?: string;
    asset_sub_group_id?: string;
    subsidiary_id?: string;
    location_id?: string;
    search?: string;
    limit?: number;
    offset?: number;
    exclude_subsidiary_id?: string;
    exclude_location_id?: string;
    item_master_id?: string;
  }): Observable<any> {
    const url = `${this.assetUrl}/api/v1/assets/`;
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.asset_group_id) params = params.set('asset_group_id', filters.asset_group_id);
    if (filters.asset_sub_group_id) params = params.set('asset_sub_group_id', filters.asset_sub_group_id);
    if (filters.subsidiary_id) params = params.set('subsidiary_id', filters.subsidiary_id);
    if (filters.location_id) params = params.set('location_id', filters.location_id);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.limit) params = params.set('limit', filters.limit.toString());
    if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
    if (filters.exclude_subsidiary_id) params = params.set('exclude_subsidiary_id', filters.exclude_subsidiary_id);
    if (filters.exclude_location_id) params = params.set('exclude_location_id', filters.exclude_location_id);
    if (filters.item_master_id) params = params.set('item_master_id', filters.item_master_id);
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  getAssetsByGrnId(grnId: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/assets/`;
    let params = new HttpParams();
    params = params.set('grn_id', grnId);
    params = params.set('limit', '100');
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  getUsers(filters?: { name?: string; limit?: number; offset?: number; is_active?: boolean }): Observable<any> {
    const url = `${this.assetUrl}/api/v1/users/`;
    let params = new HttpParams();
    if (filters) {
      if (filters.name) params = params.set('name', filters.name);
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
      if (filters.is_active !== undefined) params = params.set('is_active', filters.is_active.toString());
    }
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  // ============================================
  // ASSET ASSIGNMENT APIs
  // ============================================

  bulkAssignAssets(fulfillmentId: string, assignments: {
    asset_id: string;
    assigned_to_user_id?: string | null;
    assigned_to_department_id?: string | null;
  }[]): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-request-fulfillments/${fulfillmentId}/assign`;
    return this.http.post<any>(url, {
      fulfillment_id: fulfillmentId,
      assignments
    }, { withCredentials: true });
  }
}
